import { ApolloError } from '@apollo/client';
import { translate } from '@thoughtspot/i18n';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import {
    OptionsGroup,
    SelectOption,
} from '@thoughtspot/radiant-react/widgets/select/base-select';
import { WizardModalProps } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import _ from 'lodash';
import {
    AnalysisState,
    AnalysisType,
    SpotIqa3AnalysisConfigResponse,
    Vendor,
} from '/@services/generated/graphql-types';
import {
    A3AnalysisQuestionType,
    FORECASTED_DATAPOINTS_GAP,
    getVendorInfoForAnalysisType,
    ModalView,
} from '../spotiq-box/spotiq-box.util';
import styles from './spotiq-modal.module.scss';

export enum ModalActions {
    CONFIRM = 'CONFIRM',
    CANCEL = 'CANCEL',
}

export enum AnalysisSettingsType {
    ForecastedDatapoints,
    AnalysisVendor,
    SelectColumns,
}

export interface AnalysisStatus {
    state: AnalysisState;
    progress: number;
    analysisResultId: string;
    message: string;
    expiryDeadline?: number;
}

export interface VendorCardParams {
    vendor: Vendor;
    disabledTooltip?: string;
}

export const getAnalysisSettingsStepTitle = (
    selectedQuestionType: A3AnalysisQuestionType,
) => {
    switch (selectedQuestionType) {
        case A3AnalysisQuestionType.Forecast:
            return translate('spotIQ.modal.stepTitle.analysisSettings');
        default:
            return translate('spotIQ.modal.stepTitle.selectColumns');
    }
};

export const getAnalysisSettingsTypeForQuestion = (
    selectedQuestionType: A3AnalysisQuestionType,
    supportedVendors: VendorCardParams[],
): AnalysisSettingsType[] => {
    switch (selectedQuestionType) {
        case A3AnalysisQuestionType.Forecast:
            return [
                AnalysisSettingsType.ForecastedDatapoints,
                // Hide the analysis vendor section if number of supportedVendors is 1 (i.e. ThoughtSpot by default)
                supportedVendors.length > 1
                    ? AnalysisSettingsType.AnalysisVendor
                    : null,
                AnalysisSettingsType.SelectColumns,
            ];
        case A3AnalysisQuestionType.KeyDrivers:
            return [
                AnalysisSettingsType.AnalysisVendor,
                AnalysisSettingsType.SelectColumns,
            ];
        default:
            return [AnalysisSettingsType.SelectColumns];
    }
};

/**
 * Method to fetch the forecasted datapoints options for Forecasting spotiq analysis.
 * Hardcoded for now, later to be fetched from the BE
 */
export const getForecastedDatapointsOptions = (
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
): OptionsGroup[] => {
    const tsForecastConfig = getVendorInfoForAnalysisType(
        a3AnalysisConfig,
        AnalysisType.Forecast,
        Vendor.Thoughtspot,
    );
    const maxForecastedDatapoints = tsForecastConfig?.forecastHorizon;

    const options: SelectOption[] = [];

    // Generate options from 5 to maxForecastedDatapoints with a step value of FORECASTED_DATAPOINTS_GAP
    _.range(5, maxForecastedDatapoints + 1, FORECASTED_DATAPOINTS_GAP).forEach(
        option => {
            options.push({
                id: option.toString(),
                label: option.toString(),
            });
        },
    );

    return [{ options }];
};

export const getAnalysisVendorName = (analysisVendor: Vendor) => {
    switch (analysisVendor) {
        case Vendor.Datarobot:
            return 'DataRobot';
        case Vendor.Thoughtspot:
            return 'ThoughtSpot';
        default:
            return '';
    }
};

export const getAnalysisVendorIconID = (analysisVendor: Vendor): IconID => {
    switch (analysisVendor) {
        case Vendor.Datarobot:
            return IconID.DATAROBOT;
        case Vendor.Thoughtspot:
            return IconID.THOUGHTSPOT;
        default:
            return IconID.ILLUSTRATION;
    }
};

export const getStepForwardButtonProps = (
    modalView: ModalView,
    onClickNextButton: (_currentStep: number, callback: () => void) => void,
    setCurrentModalView: (view: ModalView) => void,
    dismissModal: () => void,
    needsAnalysisSettings = false,
    a3AnalysisSubmissionLoading = false,
    a3AnalysisColumnsDataLoading = false,
    a3AnalysisColumnsDataError = null as ApolloError,
    attributesLimitAlert = '',
    noAlgorithmSelected = false,
) => {
    switch (modalView) {
        case ModalView.CUSTOMISE:
            return {
                text: translate('spotIQ.modal.back.button'),
                isDisabled: noAlgorithmSelected,
                onNext: () => setCurrentModalView(ModalView.SELECT_COLUMNS),
            };
        case ModalView.ANALYSIS_BEGUN:
            return {
                text: translate('spotIQ.modal.done.button'),
                onNext: dismissModal,
            };
        case ModalView.SELECT_COLUMNS:
            return {
                text: translate('spotIQ.modal.analyze.button'),
                onNext: onClickNextButton,
                isDisabled:
                    !!a3AnalysisSubmissionLoading ||
                    !!attributesLimitAlert ||
                    !!a3AnalysisColumnsDataLoading ||
                    !!a3AnalysisColumnsDataError,
                isLoading: !!a3AnalysisSubmissionLoading,
            };
        case ModalView.BUSINESS_QUESTION:
            return {
                text: needsAnalysisSettings
                    ? translate('spotIQ.modal.next.button')
                    : translate('spotIQ.modal.analyze.button'),
                onNext: onClickNextButton,
                isDisabled:
                    !needsAnalysisSettings &&
                    (!!a3AnalysisSubmissionLoading ||
                        !!a3AnalysisColumnsDataLoading ||
                        !!a3AnalysisColumnsDataError),
                isLoading:
                    !needsAnalysisSettings && !!a3AnalysisSubmissionLoading,
            };
        default:
            return {
                text: translate('spotIQ.modal.next.button'),
                onNext: onClickNextButton,
            };
    }
};

export const getModalDenyButtonProps = (
    modalView: ModalView,
    selectedQuestionType: A3AnalysisQuestionType,
    openCustomizeParameters: () => void,
    resetToDefault: () => void,
) => {
    switch (modalView) {
        case ModalView.CUSTOMISE:
            return {
                text: translate('a3.reset'),
                onDeny: resetToDefault,
            };
        case ModalView.SELECT_COLUMNS:
            // Don't show customize attributes option for Forecast analysis
            if (selectedQuestionType === A3AnalysisQuestionType.Forecast) {
                return null;
            }
            return {
                text: translate('a3.customizeParameters'),
                onDeny: openCustomizeParameters,
            };
        default:
            return null;
    }
};

export const getWizardModalCurrentStep = (
    modalView: ModalView,
    selectedQuestionType: A3AnalysisQuestionType,
) => {
    switch (modalView) {
        case ModalView.CUSTOMISE:
            return 1;
        case ModalView.SELECT_COLUMNS:
            return 1;
        case ModalView.ANALYSIS_BEGUN:
            return selectedQuestionType === A3AnalysisQuestionType.KeyDrivers
                ? 1
                : 2;
        default:
            return 0;
    }
};

export const getSpotIQModalProps = (
    modalView: ModalView,
    selectedQuestionType: A3AnalysisQuestionType,
    isModalOpen: boolean,
    dismissModal: () => void,
    a3AnalysisConfigLoading: boolean,
    isCustomAnalysisLoading: boolean,
    onAfterClose: () => void,
    getSpotIQModalSteps: (modalView: ModalView) => any,
    openCustomizeParameters: () => void,
    resetToDefault: () => void,
): WizardModalProps => {
    const denyButtonProps = getModalDenyButtonProps(
        modalView,
        selectedQuestionType,
        openCustomizeParameters,
        resetToDefault,
    );
    const currentStep = getWizardModalCurrentStep(
        modalView,
        selectedQuestionType,
    );

    return {
        title: translate('spotIQ.modal.title'),
        isOpen: isModalOpen,
        denyButton: denyButtonProps,
        wizardModalStep: getSpotIQModalSteps(modalView),
        className: styles.spotiqModal,
        isProgressBarEnabled:
            modalView !== ModalView.CUSTOMISE &&
            modalView !== ModalView.SPLASH_SCREEN,
        includesHeader: modalView !== ModalView.SPLASH_SCREEN,
        includesFooter: modalView !== ModalView.SPLASH_SCREEN,
        hasCrossButton: modalView === ModalView.SPLASH_SCREEN,
        currentStep,
        onDismiss: dismissModal,
        isContentLoading: a3AnalysisConfigLoading || isCustomAnalysisLoading,
        onAfterClose,
        customFooterClassName: styles.spotiqModalFooter,
    };
};

export const analysisInProgress: AnalysisStatus = {
    state: AnalysisState.Running,
    progress: 0,
    analysisResultId: '',
    message: '',
};

export const getQuestionCardCustomClass = (id: string): string => {
    const classMap: Record<string, string> = {
        forecast_what_if: styles.forecastWhatIfCard,
        outlier: styles.outlierCard,
        trend: styles.trendCard,
        cross_correlation: styles.crossCorrelationCard,
    };
    return classMap[id] || '';
};
